import { motion } from 'framer-motion';
import { MapPin, Phone, Navigation } from 'lucide-react';
import Reveal, { easeOut } from './Reveal';

const IMG_TABLELANDS = '/area-mareeba.jpg';

const TOWNS = [
  'Mareeba',
  'Atherton',
  'Kuranda',
  'Malanda',
  'Yungaburra',
  'Dimbulah',
  'Millaa Millaa',
  'Herberton',
  'Ravenshoe',
];

export default function Area() {
  return (
    <section id="areas" className="relative bg-navy text-white py-24 lg:py-32 overflow-hidden">
      <div
        className="absolute inset-0 -z-0 opacity-60"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 15% 20%, rgba(6,182,212,0.18), transparent), radial-gradient(ellipse 50% 40% at 90% 80%, rgba(2,132,199,0.22), transparent)',
        }}
      />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-cyan-300">Where we work</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.08]">
              From Mareeba to every corner of{' '}
              <span className="font-accent italic font-normal text-cyan-300">the Tablelands</span>
            </h2>
            <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-xl">
              We are based in Mareeba and out on the road every day, supporting NDIS participants across
              the Atherton Tablelands and surrounding communities.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="area-town-chips">
              {TOWNS.map((t) => (
                <span
                  key={t}
                  data-testid={`area-chip-${t.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/90 backdrop-blur transition-colors hover:border-cyan-300/60 hover:bg-cyan-400/10"
                >
                  <MapPin size={13} className="text-cyan-300" />
                  {t}
                </span>
              ))}
              <span className="flex items-center gap-1.5 rounded-full border border-cyan-300/50 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-200">
                <Navigation size={13} />
                …and all surrounding communities
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <a
              data-testid="area-cta-call"
              href="tel:0473619278"
              className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-white text-navy px-7 py-4 font-display font-bold transition-all hover:bg-cyan-300 hover:-translate-y-0.5"
            >
              <Phone size={17} />
              Call to check availability in your area
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: easeOut }}
            className="relative overflow-hidden rounded-[2.5rem] shadow-lift ring-1 ring-white/15"
          >
            <img
              src={IMG_TABLELANDS}
              alt="Aerial view of Mareeba township, Queensland"
              className="h-[380px] lg:h-[520px] w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
          </motion.div>
          <div
            data-testid="area-badge-homebase"
            className="absolute -bottom-5 right-6 lg:right-10 flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-lift"
          >
            <span className="grid place-items-center h-10 w-10 rounded-full bg-sky-100 text-azure">
              <MapPin size={18} />
            </span>
            <div>
              <p className="font-display font-bold text-slate-900 text-sm">Mareeba QLD</p>
              <p className="text-xs text-slate-500">Our home base</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
